import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { alternatePath, EN_ONLY_PATHS, stripPrefix } from "@/i18n/LanguageContext";

// Canonicals and social URLs ALWAYS point at the public production origin —
// never at window.location, so preview/staging domains can't leak into search indexes.
const SITE_ORIGIN = (process.env.REACT_APP_PUBLIC_SITE_URL || "").replace(/\/$/, "");

const setMeta = (attr, key, content) => {
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
};

const setCanonical = (href) => {
  let el = document.head.querySelector('link[rel="canonical"]');
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", "canonical");
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
};

const setHreflang = (hreflang, href) => {
  let el = document.head.querySelector(`link[hreflang="${hreflang}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", "alternate");
    el.setAttribute("hreflang", hreflang);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
};

export const useSeo = ({ title, description, image, noindex = false }) => {
  const { pathname } = useLocation();
  useEffect(() => {
    const origin = SITE_ORIGIN || window.location.origin;
    const isProduction = SITE_ORIGIN && window.location.origin.replace(/\/$/, "") === SITE_ORIGIN;
    const absolute = (u) => (u && u.startsWith("/") ? `${origin}${u}` : u);
    const url = `${origin}${pathname}`;
    const isNl = pathname === "/nl" || pathname.startsWith("/nl/");
    if (title) {
      document.title = title;
      setMeta("property", "og:title", title);
      setMeta("name", "twitter:title", title);
    }
    if (description) {
      setMeta("name", "description", description);
      setMeta("property", "og:description", description);
      setMeta("name", "twitter:description", description);
    }
    if (image) {
      setMeta("property", "og:image", absolute(image));
      setMeta("name", "twitter:image", absolute(image));
      setMeta("name", "twitter:card", "summary_large_image");
    }
    setMeta("property", "og:url", url);
    setMeta("property", "og:type", "website");
    setMeta("property", "og:locale", isNl ? "nl_NL" : "en_GB");
    setMeta("property", "og:locale:alternate", isNl ? "en_GB" : "nl_NL");
    setMeta("name", "robots", noindex || !isProduction ? "noindex, nofollow" : "index, follow");
    setCanonical(url);

    // Reciprocal hreflang for EN <-> NL page pairs (skipped on EN-only utility pages)
    ["en", "nl", "x-default"].forEach((h) => {
      const el = document.head.querySelector(`link[hreflang="${h}"]`);
      if (el) el.remove();
    });
    const base = stripPrefix(pathname);
    const enOnly = EN_ONLY_PATHS.some((p) => base === p || base.startsWith(`${p}/`));
    if (!noindex && !enOnly) {
      const enUrl = `${origin}${alternatePath(pathname, "en")}`;
      const nlUrl = `${origin}${alternatePath(pathname, "nl")}`;
      setHreflang("en", enUrl);
      setHreflang("nl", nlUrl);
      setHreflang("x-default", enUrl);
    }
  }, [title, description, image, noindex, pathname]);
};
